import { Button, TmdxChip, TruncatedWithTooltip } from '@ds';
import { Collapse, ConditionalRender } from '@ui';
import { PropsWithChildren, useEffect, useState } from 'react';

import { useToggle } from 'react-use';
import { MdExpandMore } from 'react-icons/md';
import cn from 'classnames';
import { DonorType, OrganType } from '@/api/apis/case';
import {
  AcceptanceCriteriaBiopsy,
  AcceptanceCriteriaWarmIschemicTimeType,
  OrganAcceptanceCriteriaFields,
} from '@/api/apis/cases';
import { organAcceptanceCriteriaBiopsyNames } from '@/constants/organ-acceptance-criteria.constants';
import { ORGAN_ICON, yesNo } from '@/utils/helpers';
import SectionRow from '../SectionRow/SectionRow';
import {
  SATURATION_TIME_INITIATION_OPTIONS,
  SYSTOLIC_TIME_INITIATION_OPTIONS,
  WARM_ISCHEMIC_TIME_PREDEFINED_OPTIONS,
} from './config';

const AcceptanceCriteriaPlaceholder = ({ organType }: { organType: OrganType }) => {
  const OrganIcon = ORGAN_ICON[organType];

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-8">
      {OrganIcon && <OrganIcon className="size-[70px] text-base-60 [&_*]:!fill-current" />}
      <span className="text-hs font-semibold text-base-40">No acceptance criteria defined</span>
    </div>
  );
};

const AcceptanceDataBlock = ({ children }: PropsWithChildren) => (
  <div className="flex flex-col gap-3 bg-base-90 py-2 px-3 rounded-lg">{children}</div>
);

type AcceptanceCriteriaRowsProps = {
  organType: OrganType;
  donorType: DonorType;
  organAcceptanceCriteria: OrganAcceptanceCriteriaFields;
  useExpanded?: boolean;
};

const getWarmIshemicTimeOption = (warmIschemicTimeType?: AcceptanceCriteriaWarmIschemicTimeType) =>
  WARM_ISCHEMIC_TIME_PREDEFINED_OPTIONS.find(({ value }) => value === warmIschemicTimeType)?.name;

export const AcceptanceCriteriaRows = ({
  organType,
  donorType,
  organAcceptanceCriteria,
  useExpanded = false,
}: AcceptanceCriteriaRowsProps) => {
  const [isExpanded, toggleExpanded] = useToggle(false);
  const [acceptanceCriteriaKey, setAcceptanceCriteriaKey] = useState<number>(0);

  useEffect(() => {
    setAcceptanceCriteriaKey((prev) => prev + 1);
  }, [organAcceptanceCriteria]);

  const renderMoreRows = () => {
    return (
      <>
        <AcceptanceDataBlock>
          <SectionRow label="Functional Warm Ischemic Time Ends at Cross Clamp" truncate={false}>
            <ConditionalRender show={!!organAcceptanceCriteria.warmIschemicTimeEnds} placeholder="-">
              <div className="text-s-r text-base-20">{organAcceptanceCriteria.warmIschemicTimeEnds}</div>
            </ConditionalRender>
          </SectionRow>
        </AcceptanceDataBlock>
        <AcceptanceDataBlock>
          <SectionRow label="Total Functional Warm Ischemic Time" truncate={false}>
            <ConditionalRender show={typeof organAcceptanceCriteria.totalWarmIschemicTime === 'number'} placeholder="-">
              <TmdxChip size="sm">{organAcceptanceCriteria.totalWarmIschemicTime} min</TmdxChip>
            </ConditionalRender>
          </SectionRow>
        </AcceptanceDataBlock>
        <AcceptanceDataBlock>
          <SectionRow label="Extra Donor Tissue Requests" truncate={false}>
            <ConditionalRender show={!!organAcceptanceCriteria.extraDonorTissueRequests} placeholder="-">
              <div className="text-s-r text-base-20">{organAcceptanceCriteria.extraDonorTissueRequests}</div>
            </ConditionalRender>
          </SectionRow>
        </AcceptanceDataBlock>
      </>
    );
  };

  const warmIshemicTimeOption = getWarmIshemicTimeOption(organAcceptanceCriteria.warmIschemicTimeType);

  const getSystolicDisplay = (value?: number) => {
    if (value === SYSTOLIC_TIME_INITIATION_OPTIONS[1]) return 'Not considered';
    return `Less than ${value} mmHg`;
  };

  const getSaturationDisplay = (value?: number) => {
    if (value === SATURATION_TIME_INITIATION_OPTIONS[1]) return 'Not considered';
    if (value === SATURATION_TIME_INITIATION_OPTIONS[0]) return `Less than ${value} %`;
    return `${value} %`;
  };

  const renderAcceptanceCriteria = () => {
    switch (organType) {
      case OrganType.Heart:
        return donorType === DonorType.DCD ? (
          <div className="flex flex-col gap-2">
            <AcceptanceDataBlock>
              <SectionRow label="Total Time from Extubation" truncate={false}>
                <ConditionalRender
                  show={typeof organAcceptanceCriteria.totalExtubationTime === 'number'}
                  placeholder="-"
                >
                  <TmdxChip size="sm">{organAcceptanceCriteria.totalExtubationTime} min</TmdxChip>
                </ConditionalRender>
              </SectionRow>
            </AcceptanceDataBlock>
            <div className="text-s-m text-base-25 mx-2">Functional Warm Ischemic Time Initiation</div>
            <AcceptanceDataBlock>
              <SectionRow label="Systolic" truncate={false}>
                <ConditionalRender
                  show={typeof organAcceptanceCriteria.systolicTimeInitiation === 'number'}
                  placeholder="-"
                >
                  {typeof organAcceptanceCriteria.systolicTimeInitiation === 'number' && (
                    <TmdxChip size="sm">{getSystolicDisplay(organAcceptanceCriteria.systolicTimeInitiation)}</TmdxChip>
                  )}
                </ConditionalRender>
              </SectionRow>
              <SectionRow label="Saturation" truncate={false}>
                <ConditionalRender
                  show={typeof organAcceptanceCriteria.saturationTimeInitiation === 'number'}
                  placeholder="-"
                >
                  {typeof organAcceptanceCriteria.saturationTimeInitiation === 'number' && (
                    <TmdxChip size="sm">
                      {getSaturationDisplay(organAcceptanceCriteria.saturationTimeInitiation)}
                    </TmdxChip>
                  )}
                </ConditionalRender>
              </SectionRow>
              <SectionRow label="Clock Restarts if Vital Sign Rebounds within 5 Minutes of Initiation" truncate={false}>
                <ConditionalRender show={organAcceptanceCriteria.clockRestarts !== undefined} placeholder="-">
                  <TmdxChip size="sm">{yesNo(organAcceptanceCriteria.clockRestarts)}</TmdxChip>
                </ConditionalRender>
              </SectionRow>
            </AcceptanceDataBlock>

            {useExpanded ? (
              <>
                <Collapse isExpanded={isExpanded} className="flex flex-col pb-2 gap-2">
                  {renderMoreRows()}
                </Collapse>
                <div className="flex items-center max-h-6 px-4">
                  <Button
                    variant="light"
                    size="sm"
                    disableRipple
                    className="-ml-3 px-3"
                    endContent={<MdExpandMore className={cn({ 'rotate-180': isExpanded })} />}
                    onClick={toggleExpanded}
                  >
                    Show {isExpanded ? 'Less' : 'More'}
                  </Button>
                </div>
              </>
            ) : (
              <>{renderMoreRows()}</>
            )}
          </div>
        ) : (
          <AcceptanceDataBlock>
            <SectionRow label="Extra Donor Tissue Requests" truncate={false}>
              <ConditionalRender show={!!organAcceptanceCriteria.extraDonorTissueRequests} placeholder="-">
                <div className="text-s-r text-base-20">{organAcceptanceCriteria.extraDonorTissueRequests}</div>
              </ConditionalRender>
            </SectionRow>
          </AcceptanceDataBlock>
        );
      case OrganType.Liver:
        return donorType === DonorType.DCD ? (
          <div className="flex flex-col gap-2">
            <AcceptanceDataBlock>
              <SectionRow label="Total Time from Extubation" truncate={false}>
                <ConditionalRender
                  show={typeof organAcceptanceCriteria.totalExtubationTime === 'number'}
                  placeholder="-"
                >
                  <TmdxChip size="sm">{organAcceptanceCriteria.totalExtubationTime} min</TmdxChip>
                </ConditionalRender>
              </SectionRow>
            </AcceptanceDataBlock>
            <AcceptanceDataBlock>
              <SectionRow label="Functional Warm Ischemic Time" truncate={false}>
                <ConditionalRender show={!!warmIshemicTimeOption} placeholder="-">
                  <TmdxChip size="sm" className="min-w-0 w-full">
                    <TruncatedWithTooltip value={`${warmIshemicTimeOption}`} />
                  </TmdxChip>
                </ConditionalRender>
              </SectionRow>
              <SectionRow label="Time" truncate={false}>
                <ConditionalRender show={typeof organAcceptanceCriteria.warmIschemicTime === 'number'} placeholder="-">
                  <TmdxChip size="sm">{organAcceptanceCriteria.warmIschemicTime} min</TmdxChip>
                </ConditionalRender>
              </SectionRow>
            </AcceptanceDataBlock>
            <AcceptanceDataBlock>
              <SectionRow label="Time From Extubation to Cold Flush" truncate={false}>
                <ConditionalRender
                  show={typeof organAcceptanceCriteria.fromExtubationToColdFlush === 'number'}
                  placeholder="-"
                >
                  <TmdxChip size="sm">{organAcceptanceCriteria.fromExtubationToColdFlush} min</TmdxChip>
                </ConditionalRender>
              </SectionRow>
            </AcceptanceDataBlock>
            <AcceptanceDataBlock>
              <SectionRow label="Biopsy" truncate={false}>
                <ConditionalRender show={!!organAcceptanceCriteria.biopsyType} placeholder="-">
                  <TmdxChip size="sm">
                    {organAcceptanceCriteriaBiopsyNames[organAcceptanceCriteria.biopsyType as AcceptanceCriteriaBiopsy]}
                  </TmdxChip>
                </ConditionalRender>
              </SectionRow>
            </AcceptanceDataBlock>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <AcceptanceDataBlock>
              <SectionRow label="Biopsy" truncate={false}>
                <ConditionalRender show={!!organAcceptanceCriteria.biopsyType} placeholder="-">
                  <TmdxChip size="sm">
                    {organAcceptanceCriteriaBiopsyNames[organAcceptanceCriteria.biopsyType as AcceptanceCriteriaBiopsy]}
                  </TmdxChip>
                </ConditionalRender>
              </SectionRow>
            </AcceptanceDataBlock>
          </div>
        );
      default:
        return <AcceptanceCriteriaPlaceholder organType={organType} />;
    }
  };

  return <div key={acceptanceCriteriaKey}>{renderAcceptanceCriteria()}</div>;
};
